<template>
  <div class="group -middle -full -bg-g-02 -vs-small -hs-medium -flex -between">
    <div class="bag-item -flex">
      <div class="item-image">
        <div class="content-thumbnail-holder">
          <card-src-set-image
            type="thumb-130"
            :img-prefix="`${userAuthStore.cdn.siteAssetsPrefix}/${freeGiftProduct.img_dir}`"
            :image="freeGiftProduct.image"
            :title="freeGiftProduct.title"
          />
        </div>
      </div>
      <div class="item-info">
        <div class="item-details">Give a free pass to friends or family</div>
        <h1 class="item-title">{{ freeGiftProduct.title }}</h1>

        <div v-if="declined" class="standard-form item-more-info">
          <div class="gift-purchase-options group -flex -between">
            <div class="option-button">
              <icon-button
                :reduce="false"
                :classes="'-ico-txt -white -no-case'"
                icon="redeem"
                label="Send Free Pass"
                :submitting="processing"
                :aria-expanded="(!declined).toString()"
                :aria-controls="expanderId"
                aria-label="Expand Gift Message Form"
                @click-action="toggleGiftForm"
              ></icon-button>
            </div>
          </div>
        </div>

        <gift-message-form
          v-else
          :id="expanderId"
          form-type="freeGift"
          :init-focus-input="initFocusInput"
          :cart-item-id="cartItemId"
          :self-action-enabled="false"
          :init-form-data="initFormData"
          :error-bag="errorBag"
        />
      </div>
    </div>

    <div class="bag-action">
      <icon-button
        v-if="!declined"
        icon="close"
        classes="-ico-txt -no-case -white"
        label="No Thanks"
        :aria-expanded="(!declined).toString()"
        :aria-controls="expanderId"
        :submitting="processing"
        aria-live="polite"
        @click-action="toggleGiftForm"
      />
    </div>

    <!-- always free -->
    <div class="bag-price -item-price">
      <span v-if="!declined" class="normal-price">Free</span>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 *
 */
import { computed, ref } from 'vue'
import IconButton from '@/components/buttons/icon-button.vue'
import { fbApi } from '@/utils/http-api'
import type { ErrorBag } from '@/types/global-types'
import type { PlusPass } from '@/types/search-types'
import type { GiftFormData } from '@/types/model-types'
import GiftMessageForm from '@/components/cart/gift-message-form.vue'
import CardSrcSetImage from '@/components/images/card-src-set-image.vue'
import { useUserAuthStore } from '@/stores/user-auth'

/** PROPS **/
const props = withDefaults(
  defineProps<{
    cartItemId: string
    freeGiftProduct: PlusPass
    errorBag?: ErrorBag | null
    initFormData: GiftFormData
  }>(),
  {
    errorBag: null,
  },
)

/** STATE **/
const userAuthStore = useUserAuthStore()
const error = ref('')
const processing = ref(false)
const declined = ref(!!props.initFormData.decline)
const initFocusInput = ref(false)

/** COMPUTED **/
const expanderId = computed(() => `gift-form-${props.cartItemId}`)

/** LIFECYCLE **/

/** METHODS **/
async function toggleGiftForm() {
  // no double submit
  if (processing.value) {
    return
  }

  // reset
  error.value = ''
  processing.value = true

  // instantly toggle gift form in the ui
  const prevDeclined = declined.value
  declined.value = !prevDeclined

  // focus first input on form open
  if (!declined.value) {
    initFocusInput.value = true
  }

  try {
    // decline only
    await fbApi.put(`/store/cart`, {
      json: {
        id: props.cartItemId,
        free_gift_decline: declined.value ? 1 : 0,
      },
    })
  } catch (err) {
    console.error(err)
    error.value = 'Error declining, please try again'
    declined.value = prevDeclined
  } finally {
    processing.value = false
  }
}
</script>
